import { TrashIcon } from '@heroicons/react/24/outline';
import { t } from 'i18next';
import { Input, makeCurrencyOptions } from '@rabbit/elements/shared-components';
import { Form, Formik, FormikErrors } from 'formik';
import { useContext, useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
import { RepairWorkRegistry_PartValuesShape } from '@rabbit/bizproc/core';
import { AppContext } from '@rabbit/app-context';
import { TenantInfo } from '@rabbit/data/types';
import { VeilKeys } from '@rabbit/mixmaster/veils';
import { FBD_Vendable, FBD_Vendable_Private } from '@rabbit/data/types';

export interface PartsElementProps {
  handleDelete: () => void;
  onChange?: (
    partsValues: RepairWorkRegistry_PartValuesShape[],
    formErrors?: FormikErrors<FormValuesShape>
  ) => void;
  id: string;
  setPartsValues: React.Dispatch<
    React.SetStateAction<RepairWorkRegistry_PartValuesShape[]>
  >;
  partsValues: RepairWorkRegistry_PartValuesShape[];
}

const validationSchema = Yup.object().shape({
  part_name: Yup.string().required('Please enter a part name').trim(),
  model_id: Yup.string().required('Please enter a model id').trim(),
  parts_quantity: Yup.string().required('Please enter part quantity').trim(),
  parts_cost: Yup.object()
    .shape({
      amount: Yup.string().required('Please enter a part cost').trim(),
      currency: Yup.string(),
    })
    .required('Please enter a part cost'),
});

interface FormValuesShape {
  part_name: string;
  model_id: string;
  parts_quantity: string;
  parts_cost: {
    amount: string;
    currency: string;
  };
  vendableId?: string;
  id: string;
}

export function PartsElement({
  handleDelete,
  onChange,
  id,
  setPartsValues,
  partsValues,
}: PartsElementProps) {
  const { tenantInfo, config } = useContext(AppContext);
  const allowedCurrencies = makeCurrencyOptions(tenantInfo as TenantInfo);
  const initialValues: FormValuesShape = {
    part_name: '',
    model_id: '',
    parts_quantity: '',
    parts_cost: {
      amount: '',
      currency: tenantInfo?.currency ?? '',
    },
    id: '',
  };

  const [formValues, setFormValues] = useState<FormValuesShape>(initialValues);
  const [formErrors, setFormErrors] = useState<
    FormikErrors<FormValuesShape> | undefined
  >(undefined);
  const [manualName, setManualName] = useState<boolean>(false);

  useEffect(() => {
    const otherPartValues: RepairWorkRegistry_PartValuesShape[] =
      partsValues.filter((part) => part.id !== id);
    otherPartValues.push({
      ...formValues,
      id,
    });

    onChange && onChange(otherPartValues, formErrors);

    setPartsValues(otherPartValues);
  }, [formValues, formErrors]);

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={() => void 0}
      validationSchema={validationSchema}
      innerRef={(formikActions) =>
        formikActions
          ? setFormValues(formikActions.values)
          : setFormValues({
              id: '',
              part_name: '',
              model_id: '',
              parts_quantity: '',
              parts_cost: {
                amount: '',
                currency: tenantInfo?.currency || '',
              },
            })
      }
    >
      {({ errors, values, touched, setFieldValue }) => {
        Object.keys(touched).length > 0 && setFormErrors(errors);
        return (
          <Form className="flex flex-col gap-3" id={id}>
            <div className="flex items-center gap-4">
              <div className="w-[361px]">
                {config?.VENDABLES?.PART_SEARCH?.ENABLED && !manualName ? (
                  <Input
                    type="autocomplete-vendable-part"
                    label=""
                    name="part_name"
                    settings={{
                      id: 'part_name',
                      placeholder: 'Part name',
                      tenantLink: t(VeilKeys.TENANT_LINK),
                      hint: '*required',
                      customButton: (
                        <button
                          type="button"
                          className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-left text-sm font-semibold text-gray-700 hover:bg-gray-50"
                          onClick={() => setManualName(true)}
                        >
                          {t("Can't find the part? Enter manually")}
                        </button>
                      ),
                    }}
                    onChange={async (e: any) => {
                      if (!e?.id) return;
                      try {
                        const vendableId = e.id as string;
                        const vendable = await FBD_Vendable.get(vendableId);
                        const vendablePrivate = await FBD_Vendable_Private.get(vendableId);
                        if (vendable) {
                          void setFieldValue('part_name', vendable.title ?? '');
                          void setFieldValue('model_id', vendable.mpn ?? '');
                          void setFieldValue('parts_cost', {
                            amount: vendablePrivate?.rrp?.amount?.toString() ?? '',
                            currency: tenantInfo?.currency,
                          });
                        }
                      } catch (err) {
                        // Silent fail; user can still fill manually
                        console.error('Failed to hydrate part from selection', err);
                      }
                    }}
                  />
                ) : (
                  <div className="flex items-center gap-2">
                    <Input
                      type="text"
                      label=""
                      name="part_name"
                      settings={{
                        id: 'part_name',
                        placeholder: 'Part name',
                        hint: '*required',
                      }}
                    />
                    {config?.VENDABLES?.PART_SEARCH?.ENABLED ? (
                      <button
                        type="button"
                        className="text-sm font-semibold text-primary-900 underline"
                        onClick={() => setManualName(false)}
                      >
                        {t('Use search')}
                      </button>
                    ) : null}
                  </div>
                )}
              </div>
              <div className="max-w-[156px]">
                <Input
                  type="text"
                  label=""
                  name="model_id"
                  settings={{
                    id: 'model_id',
                    placeholder: 'Model ID',
                    hint: '*required',
                  }}
                />
              </div>
              <div className="w-[65px]">
                <Input
                  type="text"
                  name="parts_quantity"
                  settings={{
                    id: 'parts_quantity',
                    placeholder: 'Qt',
                    hint: '*required',
                  }}
                />
              </div>
              <div className="w-[280px]">
                <Input
                  type="currency"
                  name="parts_cost"
                  settings={{
                    placeholder: 'Per unit',
                    currency: tenantInfo?.currency,
                    hint: '*required',
                    inputTypeCurrencyOverrideOptions: allowedCurrencies,
                  }}
                />
              </div>
              <div
                className="bg-primary-900 rounded-md px-3 py-2"
                onClick={handleDelete}
              >
                <TrashIcon className="h-5 w-5 cursor-pointer stroke-white" />
              </div>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
}

export default PartsElement;
